<template>
  <div>
    <EasyDataTable
      ref="dataTable"
      v-model:server-options="serverOptions"
      table-class-name="vue3-easy-data-table table-shadow"
      :server-items-length="serverItemsLength"
      :loading="loading"
      :headers="headers"
      :items="items"
      :prevent-context-menu-row="false"
      :fixed-header="false"
      no-hover
      alternating
      hide-footer
      @update:server-options="updateServerOptions"
    >
      <template #loading>
        <b-spinner class="mt-5" variant="success" />
      </template>
      <template #item="{ column, item }: { column: string; item: ProxyLogItem }">
        <MessageRow :column="column" :message="item" />
      </template>
    </EasyDataTable>

    <TablePagination
      v-if="detailed"
      v-model:server-options-ref="serverOptions"
      v-model:data-table-ref="dataTable"
      :server-items-length="serverItemsLength"
    />
  </div>
</template>

<script lang="ts">
import type { ProxyLogItem } from '@/types/Models/ProxyLog'
import { BSpinner } from 'bootstrap-vue-next'
import { computed, defineComponent, onMounted, ref, watch, type ComputedRef, type Ref } from 'vue'
import { useRoute } from 'vue-router'
import { QueryParams } from '@/const/MessagesListQueryFilters'
import { makeRequestParams } from '@/services/Messages'
import { useShipmentStore } from '@/store/shipment'
import EasyDataTable, { type Header, type ServerOptions } from 'vue3-easy-data-table'
import MessageRow from '@/views/Components/Messages/Row.vue'
import TablePagination from '@/views/Components/Elements/Table/TablePagination.vue'

export default defineComponent({
  name: 'MessagesTable',
  components: {
    BSpinner,
    MessageRow,
    TablePagination,
    EasyDataTable,
  },
  props: {
    detailed: {
      type: Boolean,
      required: true,
    },
    baseFilter: {
      type: Object as () => Record<string, string>,
      required: false,
      default: () => ({}),
    },
  },
  setup(props) {
    const shipmentStore = useShipmentStore()
    const route = useRoute()

    const dataTable = ref()
    const shipmentId: ComputedRef<string | null> = computed(
      () => props.baseFilter[QueryParams.SHIPMENT_NUMBER] || props.baseFilter['item[]'] || null,
    )
    const loading: ComputedRef<boolean> = computed(() => shipmentStore.proxyLogsLoading)
    const items: ComputedRef<ProxyLogItem[]> = computed(() => shipmentStore.proxyLogsList(shipmentId.value))
    const serverItemsLength: ComputedRef<number> = computed(() => shipmentStore.proxyLogsTotal(shipmentId.value))
    const serverOptions: Ref<ServerOptions> = ref({
      page: 1,
      rowsPerPage: 50,
    })
    const headers: Header[] = [
      { text: '', value: 'icon' },
      { text: 'Time', value: 'created_at' },
      { text: 'Recipient', value: 'sent_to' },
      { text: 'Email subject / SMS text', value: 'data' },
      ...(props.detailed ? [{ text: 'Shipment', value: 'shipment' }] : []),
      ...(props.detailed ? [{ text: 'Carrier', value: 'carrier' }] : []),
    ]

    const loadFromServer = () => {
      const params = { route, serverOptions, detailed: props.detailed, baseFilter: props.baseFilter }
      shipmentStore.fetchProxyLogs(makeRequestParams(params))
    }

    const updateServerOptions = (newOptions: ServerOptions) => {
      serverOptions.value = newOptions
    }

    onMounted(loadFromServer)

    watch(serverOptions, loadFromServer, { deep: true })

    return {
      items,
      serverItemsLength,
      loading,
      serverOptions,
      headers,
      updateServerOptions,
      dataTable,
    }
  },
})
</script>
